import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { ArrowRight, Lock } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const FAQ = [
  {
    q: 'Is this a real website? Do I actually pay real money?',
    a: 'Yes — completely real. Payments are processed by Stripe, and the DLC packs genuinely unlock calculator features. The premise is a joke. The execution is not. You will be charged real money to add real numbers.',
  },
  {
    q: 'What is free on the calculator?',
    a: 'Only the number zero (0) is free. Digits 1–9, all operators (+, −, ×, ÷), the equals sign, brackets, decimal point, and the clear button all require a paid pack. This is intentional.',
  },
  {
    q: 'Why would anyone pay for this?',
    a: 'Exactly. That is the point. Millions of people pay EA Games real money to unlock things that should already be in the game. This website asks you to notice the absurdity by experiencing it firsthand — with a calculator.',
  },
  {
    q: 'What is EA Games?',
    a: 'Electronic Arts (EA) is one of the world\'s largest video game publishers, responsible for FIFA, Madden, The Sims, Battlefield, and more. They are widely considered the pioneers of modern gaming microtransactions — a practice that has spread across the entire industry.',
  },
  {
    q: 'What exactly are microtransactions?',
    a: 'Microtransactions are small in-app or in-game purchases — typically $0.99 to $9.99 — that unlock content, features, characters, or cosmetics. The controversy is that this content was previously included in the base game price. Over time, publishers began shipping incomplete games and selling the rest piece by piece.',
  },
  {
    q: 'Is this affiliated with EA Games?',
    a: 'Absolutely not. This is independent satire. EA Games, Electronic Arts, FIFA, Madden, and all related names are trademarks of Electronic Arts Inc. This website has no affiliation, endorsement, or sponsorship from EA.',
  },
  {
    q: 'What do I get after purchasing a pack?',
    a: 'Your account is permanently unlocked for that pack. Basic Math Pack ($0.99) gives you +, −, and digits 1–5. Advanced Operations Bundle ($2.99) gives you ×, ÷, and digits 6–9. The Ultimate Mathematician Edition ($9.99) gives you =, C, (, ), and the decimal point.',
  },
  {
    q: 'Can I get a refund?',
    a: 'You paid to unlock a calculator. You unlocked the calculator. Refunds are available within 24 hours by contacting us — but we will judge you for needing them.',
  },
];

const TIMELINE = [
  { year: '2006', event: 'EA releases Horse Armor DLC for The Elder Scrolls IV: Oblivion — the first major paid cosmetic in a premium game. The gaming world laughs. Then buys it.' },
  { year: '2011', event: 'Horse Armor is no longer a joke. DLC season passes become standard. $60 games now ship with $40–$80 of day-one paid content.' },
  { year: '2013', event: 'EA wins "Worst Company in America" twice from The Consumerist. Beats Bank of America. Beats Comcast. A video game company.' },
  { year: '2017', event: 'EA\'s Star Wars Battlefront II launches with a loot box system so aggressive it triggers government investigations in Belgium and Netherlands, leading to loot boxes being classified as gambling.' },
  { year: '2021', event: 'FIFA Ultimate Team generates over $1.6 billion in revenue — from a single mode in a $60 game. Players spend real money on virtual player cards.' },
  { year: '2026', event: 'We built a paywalled calculator. The circle is complete.' },
];

export default function AboutPage() {
  useEffect(() => {
    document.title = 'What is This? | CalcByEA — The Microtransaction Calculator';
  }, []);

  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-950">
      <Helmet>
        <title>What Is CalcByEA? | Microtransaction Parody Explained</title>
        <meta name="description" content="CalcByEA is a satirical calculator that parodies EA Games' microtransaction model. Every button costs money. Read the full context here." />
        <link rel="canonical" href="https://calculatorbyea.com/about" />
        <meta property="og:url" content="https://calculatorbyea.com/about" />
        <meta property="og:title" content="What Is CalcByEA? | Microtransaction Parody Explained" />
        <meta property="og:description" content="CalcByEA is a satirical calculator that parodies EA Games' microtransaction model. Every button costs money. Read the full context here." />
        <meta name="twitter:title" content="What Is CalcByEA? | Microtransaction Parody Explained" />
        <meta name="twitter:description" content="A satirical calculator that parodies EA Games' microtransaction model. Every button costs money." />
      </Helmet>
      <Navbar />

      <div className="pt-20 max-w-3xl mx-auto px-6 pb-24">

        {/* Hero */}
        <div className="border-b-2 border-zinc-200 pb-12 mb-12">
          <div className="text-[9px] font-bold uppercase tracking-[0.35em] text-zinc-400 mb-3">Context</div>
          <h1 className="font-bebas text-5xl md:text-6xl text-zinc-950 leading-tight mb-4">
            What Is<br />
            <span className="italic">This Site?</span>
          </h1>
          <p className="text-zinc-600 text-base leading-relaxed max-w-xl">
            A calculator that charges you money to use basic math. Here's everything you need
            to understand why that's funny — and why it hits a little too close to home.
          </p>
        </div>

        {/* Section 1 — What is this */}
        <section className="mb-14">
          <div className="text-[9px] font-bold uppercase tracking-[0.35em] text-zinc-400 mb-3">01</div>
          <h2 className="font-bebas text-3xl text-zinc-950 mb-4">The Premise</h2>
          <div className="space-y-4 text-zinc-600 text-[15px] leading-relaxed">
            <p>
              This is <strong className="text-zinc-950 font-semibold">CalcByEA</strong> — a fully functional web calculator
              where almost every button is locked behind a paywall. You need to buy
              "DLC packs" to unlock basic operations like addition, multiplication,
              and the equals sign.
            </p>
            <p>
              The number <strong className="text-zinc-950 font-semibold">0 is free</strong>. Everything else costs money.
              This is not a bug. This is the product.
            </p>
            <p>
              It is satire. A mirror held up to the video game industry —
              specifically to companies like EA Games, who pioneered the practice
              of charging customers extra for things that used to come included.
            </p>
          </div>

          {/* Visual */}
          <div className="mt-8 border-2 border-zinc-200 bg-white p-6">
            <div className="text-[9px] font-bold uppercase tracking-[0.3em] text-zinc-400 mb-4">What you get vs. what you pay for</div>
            <div className="grid grid-cols-2 gap-4">
              <div className="border border-zinc-100 p-4 bg-zinc-950">
                <div className="text-[9px] font-bold uppercase tracking-widest text-zinc-500 mb-3">Free (included)</div>
                <div className="font-bebas text-6xl text-white">0</div>
                <div className="text-zinc-600 text-xs mt-2">The number zero. That's it.</div>
              </div>
              <div className="border border-zinc-200 p-4">
                <div className="text-[9px] font-bold uppercase tracking-widest text-zinc-400 mb-3">Paid (DLC packs)</div>
                <div className="grid grid-cols-4 gap-1">
                  {['1','2','3','4','5','6','7','8','9','+','−','×','÷','=','C','.'].map(s => (
                    <div key={s} className="aspect-square flex items-center justify-center bg-zinc-100 border border-zinc-200 relative">
                      <span className="font-bebas text-sm text-zinc-500">{s}</span>
                      <Lock size={5} className="absolute top-0.5 right-0.5 text-zinc-400" strokeWidth={3} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 2 — EA */}
        <section className="mb-14">
          <div className="text-[9px] font-bold uppercase tracking-[0.35em] text-zinc-400 mb-3">02</div>
          <h2 className="font-bebas text-3xl text-zinc-950 mb-4">Who Is EA Games?</h2>
          <div className="space-y-4 text-zinc-600 text-[15px] leading-relaxed">
            <p>
              <strong className="text-zinc-950 font-semibold">Electronic Arts (EA)</strong> is one of the world's largest video game publishers,
              known for blockbuster franchises like FIFA, Madden NFL, The Sims, Battlefield, and Need for Speed.
            </p>
            <p>
              They are also one of the most criticized companies in the gaming industry —
              winning <strong className="text-zinc-950 font-semibold">"Worst Company in America"</strong> from The Consumerist
              two years in a row (2012 and 2013), beating banks and telecom companies.
              The reason? Aggressive monetization of their games.
            </p>
            <p>
              EA did not invent microtransactions, but they industrialized them —
              turning what began as a $2 horse armor DLC into a multi-billion dollar
              business model that reshaped the entire industry.
            </p>
          </div>
        </section>

        {/* Section 3 — Microtransactions */}
        <section className="mb-14">
          <div className="text-[9px] font-bold uppercase tracking-[0.35em] text-zinc-400 mb-3">03</div>
          <h2 className="font-bebas text-3xl text-zinc-950 mb-4">What Are Microtransactions?</h2>
          <div className="space-y-4 text-zinc-600 text-[15px] leading-relaxed">
            <p>
              A <strong className="text-zinc-950 font-semibold">microtransaction</strong> is a small in-app purchase —
              typically $0.99 to $9.99 — that unlocks content, characters, levels, or features inside
              a game or app you've already paid for.
            </p>
            <p>
              The controversy: this content was once included in the base game. Publishers
              began shipping games intentionally incomplete and selling the rest as
              "downloadable content" (DLC). What started as optional extras became required
              content, cosmetic loot boxes, pay-to-win mechanics, and battle passes.
            </p>
          </div>

          {/* Examples */}
          <div className="mt-6 space-y-2">
            {[
              { game: 'The Sims 4', detail: 'Base game is free. Full content costs ~$1,000+ in DLC packs.' },
              { game: 'FIFA Ultimate Team', detail: '$1.6B/year in revenue from card packs in a $60 sports game.' },
              { game: 'Star Wars Battlefront II', detail: 'Launched with paid loot boxes. Triggered gambling law investigations in 3 countries.' },
              { game: 'Battlefield 4', detail: 'Premium Pass DLC cost more than the base game itself.' },
            ].map(({ game, detail }) => (
              <div key={game} className="flex gap-4 border border-zinc-100 bg-white p-4">
                <div className="shrink-0 w-1 bg-zinc-950" />
                <div>
                  <div className="font-semibold text-zinc-950 text-sm">{game}</div>
                  <div className="text-zinc-500 text-xs mt-0.5">{detail}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Section 4 — Timeline */}
        <section className="mb-14">
          <div className="text-[9px] font-bold uppercase tracking-[0.35em] text-zinc-400 mb-3">04</div>
          <h2 className="font-bebas text-3xl text-zinc-950 mb-6">A Brief History of Gaming Greed</h2>
          <div className="space-y-0 border-l-2 border-zinc-200 pl-6">
            {TIMELINE.map(({ year, event }) => (
              <div key={year} className="relative pb-8 last:pb-0">
                <div className="absolute -left-[31px] top-0.5 w-4 h-4 bg-zinc-950 border-2 border-zinc-50 rounded-full" />
                <div className="font-bebas text-2xl text-zinc-950 leading-none mb-1">{year}</div>
                <p className="text-zinc-600 text-sm leading-relaxed">{event}</p>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-14">
          <div className="text-[9px] font-bold uppercase tracking-[0.35em] text-zinc-400 mb-3">05</div>
          <h2 className="font-bebas text-3xl text-zinc-950 mb-6">Frequently Asked Questions</h2>
          <div className="space-y-0 border-t-2 border-zinc-200">
            {FAQ.map(({ q, a }) => (
              <details key={q} className="group border-b-2 border-zinc-200">
                <summary className="flex items-center justify-between py-4 cursor-pointer list-none gap-4">
                  <span className="font-semibold text-zinc-950 text-sm leading-snug">{q}</span>
                  <span className="shrink-0 font-bebas text-xl text-zinc-400 group-open:rotate-45 transition-transform duration-200 origin-center">+</span>
                </summary>
                <p className="pb-5 text-zinc-600 text-sm leading-relaxed pr-8">{a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* CTA */}
        <div className="border-2 border-zinc-950 bg-zinc-950 p-8 shadow-[6px_6px_0px_0px_rgba(9,9,11,0.3)]">
          <div className="text-[9px] font-bold uppercase tracking-[0.35em] text-zinc-500 mb-3">Ready?</div>
          <h3 className="font-bebas text-4xl text-white leading-tight mb-2">
            Experience It<br />
            <span className="italic">Yourself.</span>
          </h3>
          <p className="text-zinc-400 text-sm mb-6">
            Try to add 1 + 1. We dare you.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/calculator"
              className="flex items-center gap-2 bg-white text-zinc-950 font-bold uppercase tracking-widest text-xs px-5 py-3 border-2 border-white hover:bg-zinc-100 active:scale-95 transition-all duration-100"
              data-testid="about-try-calculator"
            >
              Try the Calculator <ArrowRight size={12} strokeWidth={3} />
            </Link>
            <Link
              to="/store"
              className="flex items-center gap-2 text-zinc-400 font-bold uppercase tracking-widest text-xs px-5 py-3 border-2 border-zinc-700 hover:border-white hover:text-white active:scale-95 transition-all duration-100"
              data-testid="about-view-store"
            >
              View DLC Store
            </Link>
          </div>
        </div>

      </div>
    </div>
  );
}
